import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  IgButton,
  IgDialog,
  IgDivider,
  IgTypography,
} from "@intelligems/ig-theme";
import { ExperienceDto, ScheduleDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { useGetTestSchedules } from "../../../../../../api/schedules/get-schedules-by-experience-id";
import { getDialog, setDialog } from "../../../../../../store/slices/global";

import { BasePanel } from "./BasePanel";
import { DisabledReason } from "./types";

interface Props {
  isLoading: boolean;
  startAtSchedule: ScheduleDto | null;
  pauseAtSchedule: ScheduleDto | null;
  endAtSchedule: ScheduleDto | null;
  experience: ExperienceDto;
}

const OFFSET = 0;

const StartAt = ({ startAtSchedule, experience, isLoading }: Props) => {
  const now = useMemo(() => DateTime.now().plus({ minutes: OFFSET }), []);
  return (
    <BasePanel
      isLoading={isLoading}
      label={"Start"}
      color={"success"}
      schedule={startAtSchedule}
      experience={experience}
      minScheduleTime={now}
      action={"start"}
      disabledReason={
        experience.status === "started" ? "testStarted" : undefined
      }
    />
  );
};

const PauseAt = ({
  startAtSchedule,
  pauseAtSchedule,
  endAtSchedule,
  experience,
  isLoading,
}: Props) => {
  let disabledReason: DisabledReason | undefined = undefined;
  if (experience.status === "paused") {
    disabledReason = "testPaused";
  } else if (experience.testTypes.hasTestPricing) {
    disabledReason = "priceTest";
  } else if (experience.testTypes.hasTestShipping) {
    disabledReason = "shippingTest";
  } else if (
    experience.status !== "started" &&
    !startAtSchedule?.executionTime
  ) {
    disabledReason = "startMissing";
  } else if (endAtSchedule?.executionTime) {
    disabledReason = "endSet";
  }

  return (
    <BasePanel
      isLoading={isLoading}
      label={"Pause"}
      color={"warning"}
      schedule={pauseAtSchedule}
      minScheduleTime={
        experience.status === "started"
          ? DateTime.now().plus({ minutes: OFFSET })
          : startAtSchedule?.executionTime
            ? DateTime.fromMillis(startAtSchedule.executionTime).plus({
                minutes: OFFSET,
              })
            : undefined
      }
      action={"pause"}
      experience={experience}
      disabledReason={disabledReason}
    />
  );
};

const EndAt = ({
  startAtSchedule,
  pauseAtSchedule,
  endAtSchedule,
  experience,
  isLoading,
}: Props) => {
  let disabledReason: DisabledReason | undefined = undefined;
  if (experience.status === "ended") {
    disabledReason = "testEnded";
  } else if (experience.testTypes.hasTestPricing) {
    disabledReason = "priceTest";
  } else if (experience.testTypes.hasTestShipping) {
    disabledReason = "shippingTest";
  } else if (
    experience.status !== "started" &&
    !startAtSchedule?.executionTime
  ) {
    disabledReason = "startMissing";
  } else if (pauseAtSchedule?.executionTime) {
    disabledReason = "endSet";
  }

  return (
    <BasePanel
      isLoading={isLoading}
      label={"End"}
      color={"error"}
      schedule={endAtSchedule}
      experience={experience}
      action={"end"}
      minScheduleTime={
        experience.status === "started"
          ? DateTime.now().plus({ minutes: OFFSET })
          : startAtSchedule?.executionTime
            ? DateTime.fromMillis(startAtSchedule.executionTime).plus({
                minutes: OFFSET,
              })
            : undefined
      }
      disabledReason={disabledReason}
    />
  );
};

export const ScheduleTestDialog = () => {
  const dispatch = useDispatch();
  const dialog = useSelector(getDialog("scheduleTest"));

  const schedules = useGetTestSchedules(dialog?.props?.experience.id);

  const handleClose = () => {
    dispatch(
      setDialog({
        dialog: "scheduleTest",
        open: false,
        props: undefined,
      })
    );
  };

  const { startAtSchedule, pauseAtSchedule, endAtSchedule } = useMemo(() => {
    if (!schedules.isSuccess) {
      return {
        startAtSchedule: null,
        pauseAtSchedule: null,
        endAtSchedule: null,
      };
    }

    const startAtSchedule =
      schedules.data.schedules?.find(
        (schedule) => schedule.action === "start"
      ) || null;
    const pauseAtSchedule =
      schedules.data.schedules?.find(
        (schedule) => schedule.action === "pause"
      ) || null;
    const endAtSchedule =
      schedules.data.schedules?.find((schedule) => schedule.action === "end") ||
      null;

    return {
      startAtSchedule,
      pauseAtSchedule,
      endAtSchedule,
    };
  }, [schedules.isSuccess, schedules.data]);

  if (!dialog.props?.experience) {
    return null;
  }

  return (
    <IgDialog
      showCloseIcon={true}
      hideActions={true}
      title={
        <Grid container direction={"column"}>
          <Grid item>
            <IgTypography sx={{ fontWeight: 600, fontSize: "18px" }}>
              Schedule{" "}
              <IgTypography
                sx={{ fontStyle: "italic", fontWeight: 600, fontSize: "18px" }}
              >
                {dialog.props.experience.name}
              </IgTypography>
            </IgTypography>
          </Grid>
          <Grid item>
            <IgTypography>{DateTime.local().offsetNameLong}</IgTypography>
          </Grid>
        </Grid>
      }
      handleClose={handleClose}
      showDialog={dialog.isOpen}
      minWidth={"600px"}
      dialogHeight={null}
      maxWidth={"sm"}
      handleYesClick={() => {
        handleClose();
      }}
      noButtonText={null}
      shouldNotClose={true}
      rawContent={
        <Grid
          item
          container
          direction={"column"}
          justifyContent={"center"}
          rowSpacing={2}
          flexWrap={"nowrap"}
        >
          <Grid item>
            <StartAt
              isLoading={schedules.isLoading}
              startAtSchedule={startAtSchedule}
              pauseAtSchedule={pauseAtSchedule}
              endAtSchedule={endAtSchedule}
              experience={dialog.props.experience}
            />
          </Grid>
          <Grid item>
            <IgDivider />
          </Grid>
          <Grid item>
            <PauseAt
              isLoading={schedules.isLoading}
              startAtSchedule={startAtSchedule}
              pauseAtSchedule={pauseAtSchedule}
              endAtSchedule={endAtSchedule}
              experience={dialog.props.experience}
            />
          </Grid>
          <Grid item>
            <EndAt
              isLoading={schedules.isLoading}
              startAtSchedule={startAtSchedule}
              pauseAtSchedule={pauseAtSchedule}
              endAtSchedule={endAtSchedule}
              experience={dialog.props.experience}
            />
          </Grid>
          <Grid item>
            <IgButton variant={"contained"} onClick={handleClose}>
              Done
            </IgButton>
          </Grid>
        </Grid>
      }
    />
  );
};
